// components/WhyBookWithUs.jsx
"use client";
import React, { useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
    Skeleton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FeatureCard from "./FeatureCard";
import Heading from "./heading";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import EmailIcon from '@mui/icons-material/Email';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import Image from "next/image";

const ICON_MAP = {
    EmojiEmotionsIcon: <EmojiEmotionsIcon sx={{ color: "#7c7c7c" }} />,
    ReceiptIcon: <ReceiptIcon sx={{ color: "#7c7c7c" }} />,
    CreditCardIcon: <CreditCardIcon sx={{ color: "#7c7c7c" }} />,
    SupportAgentIcon: <SupportAgentIcon sx={{ color: "#7c7c7c" }} />,
    PersonAddAlt1Icon: <PersonAddAlt1Icon sx={{ color: "#7c7c7c" }} />,
    LocalAtmIcon: <LocalAtmIcon sx={{ color: "#7c7c7c" }} />,
    EmailIcon: <EmailIcon sx={{ color: "#7c7c7c" }} />,
    MarkChatUnreadIcon: <MarkChatUnreadIcon sx={{ color: "#7c7c7c" }} />,
    PlayCircleOutlineIcon: <PlayCircleOutlineIcon sx={{ color: "#7c7c7c" }} />,
};


export default function WhyBookWithUs({ loader, features, faqs, title = "WhyBook with us?", subtitle = "Looking for something?" }) {
    const [expanded, setExpanded] = useState(0); // open first FAQ by default

    const handleChange = (index) => (event, isExpanded) => {
        setExpanded(isExpanded ? index : false);
    };

    return (
        <Box sx={{ py: 1, textAlign: "center", px: 12, maxWidth: 1320, mx: "auto" }}>
            {/* Section Heading */}
            <Heading title={title} subtitle={subtitle} />

            {/* Feature Cards */}
            <Box sx={{ pb: 8 }}>
                <Grid container spacing={3} justifyContent="center">
                    {loader
                        ? // 🔹 Skeleton Loader for Features
                        Array.from({ length: 3 }).map((_, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <Card
                                    sx={{
                                        borderRadius: 4,
                                        height: 300,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        p: 3,
                                    }}
                                >
                                    <Skeleton
                                        variant="rectangular"
                                        width={80}
                                        height={80}
                                        sx={{ borderRadius: 2, mb: 2 }}
                                    />
                                    <Skeleton variant="text" width="60%" height={28} />
                                    <Skeleton variant="text" width="80%" />
                                    <Skeleton variant="text" width="70%" />
                                </Card>
                            </Grid>
                        ))
                        : features?.map((item, i) => (
                            <Grid item xs={12} sm={6} md={4} key={i}>
                                <FeatureCard {...item} />
                            </Grid>
                        ))}
                </Grid>
            </Box>

            {/* FAQ Accordion */}
            <Box
                sx={{
                    maxWidth: 700,
                    mx: "auto",
                    backgroundColor: "transparent",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px"
                }}
            >
                {loader
                    ? // 🔹 Skeleton Loader for FAQs
                    Array.from({ length: 4 }).map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                py: 2,
                                borderBottom: "1px solid #E0E0E0",
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                width={40}
                                height={40}
                                sx={{ borderRadius: 2 }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="60%" height={24} />
                                <Skeleton variant="text" width="90%" height={18} />
                            </Box>
                        </Box>
                    ))
                    : faqs?.map((faq, index) => (
                        <Accordion
                            key={index}
                            disableGutters
                            expanded={expanded === index}
                            onChange={handleChange(index)}
                            elevation={0}
                            sx={{
                                "&:before": { display: "none" },
                                boxShadow: "none",
                                "&.Mui-expanded": { margin: 0 },
                                backgroundColor: "transparent",
                                // borderBottom: "1px solid #E0E0E0",
                                textAlign: "start"
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: "#757575" }} />}
                                sx={{
                                    pt: 1.5,
                                    "& .MuiAccordionSummary-content": {
                                        alignItems: "center",
                                        gap: 1.5,
                                        my: 0,
                                    },
                                }}
                            >
                                {/* Icon inside bordered box */}
                                {/* Icon inside bordered box */}
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        border: "1px solid #D1D5DB",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#fff",
                                        flexShrink: 0,
                                        color: "#7c7c7c",
                                        position: "relative",   // ✅ required for Image with `fill`
                                        overflow: "hidden",      // optional but nice
                                    }}
                                >
                                    {faq.icon && (
                                        <Image
                                            src={`${process.env.IMAGE_BASE_URL}/${faq.icon}`}
                                            alt="FAQ icon"
                                            width={22}
                                            height={22}
                                            style={{ objectFit: "contain" }}
                                        />
                                    )}
                                </Box>


                                {/* Text wrapper (keeps Q & A aligned) */}
                                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "16px",
                                            color: "#000",
                                        }}
                                    >
                                        {faq.question}
                                    </Typography>
                                </Box>
                            </AccordionSummary>

                            {/* Answer perfectly aligned under question */}
                            <AccordionDetails
                                sx={{
                                    pl: `calc(40px + 1.5rem)`, // same left offset as question start
                                    pr: 2,
                                    pb: 0,
                                    pt: 0,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#555",
                                        lineHeight: 1.8,
                                        fontSize: "15px",
                                        textAlign: "start",
                                    }}
                                >
                                    {faq.answer}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
            </Box>
        </Box>
    );
}
